/**
 * /dashboard/accounting/reports/balance-sheet — Balance Sheet (G_ACC8).
 *
 * As-of date picker, Schedule III Part I grouped layout, §128(1)
 * balanced-check alert, CSV export.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/reports/balance-sheet/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatPaise } from "@/lib/expense-format";
import { balanceSheetCSVURL } from "@/lib/api/accounting";
import { useBalanceSheetReport } from "@/hooks/use-accounting";
import { toApiError } from "@/lib/api";
import type { BSLineGroup } from "@/types/accounting";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/reports/balance-sheet",
)({
  component: BalanceSheetRoute,
});

function todayUTCDate(): string {
  const d = new Date();
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function BSGroup({ group }: Readonly<{ group: BSLineGroup }>) {
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
        {group.label}
      </div>
      <div className="divide-y">
        {group.lines.length === 0 ? (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            No accounts under this head.
          </div>
        ) : (
          group.lines.map((l) => (
            <div
              key={l.account_id}
              className="grid grid-cols-[5rem_1fr_auto] gap-3 px-4 py-2 text-sm"
            >
              <span className="font-mono text-xs text-muted-foreground">
                {l.account_code}
              </span>
              <span>{l.account_name}</span>
              <span className="tabular-nums">{formatPaise(l.amount_minor)}</span>
            </div>
          ))
        )}
        <div className="grid grid-cols-[5rem_1fr_auto] gap-3 bg-muted/20 px-4 py-2 text-sm font-medium">
          <span />
          <span>{group.label} — Subtotal</span>
          <span className="tabular-nums">
            {formatPaise(group.subtotal_minor)}
          </span>
        </div>
      </div>
    </div>
  );
}

function BalanceSheetRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [asOf, setAsOf] = useState<string>(todayUTCDate());
  const [pending, setPending] = useState<string>(asOf);
  const query = useBalanceSheetReport(asOf);
  const data = query.data ?? null;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  return (
    <AccountingModuleShell
      title="Balance Sheet"
      description="Companies Act 2013 Schedule III Part I — equity, liabilities, and assets."
      actions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          <Button asChild className="gap-1.5">
            <a href={balanceSheetCSVURL(asOf)} download>
              <Download className="h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="grid gap-3 py-4 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-xs gap-1.5">
            <Label htmlFor="bs-asof">As of</Label>
            <Input
              id="bs-asof"
              type="date"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              onClick={() => setAsOf(pending)}
              disabled={!pending || pending === asOf}
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load balance sheet</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : data ? (
        <div className="mt-4 space-y-6">
          {!data.balanced && (
            <Alert variant="destructive">
              <AlertTitle>Balance Sheet is unbalanced</AlertTitle>
              <AlertDescription>
                Total Equity + Liabilities{" "}
                {formatPaise(data.total_equity_liabilities_minor)} != Total
                Assets {formatPaise(data.total_assets_minor)}. Mid-period this
                is usually unbooked P&L (run G_ACC9 period close); any other
                gap warrants investigation per Companies Act §128(1).
              </AlertDescription>
            </Alert>
          )}

          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              I. Equity and Liabilities
            </h2>
            <BSGroup group={data.equity} />
            <BSGroup group={data.non_current_liabilities} />
            <BSGroup group={data.current_liabilities} />
            <Card>
              <CardContent className="flex justify-between py-3 text-sm font-medium">
                <span>Total Equity and Liabilities</span>
                <span className="tabular-nums">
                  {formatPaise(data.total_equity_liabilities_minor)}
                </span>
              </CardContent>
            </Card>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold">II. Assets</h2>
            <BSGroup group={data.non_current_assets} />
            <BSGroup group={data.current_assets} />
            <Card>
              <CardContent className="flex justify-between py-3 text-sm font-medium">
                <span>Total Assets</span>
                <span className="tabular-nums">
                  {formatPaise(data.total_assets_minor)}
                </span>
              </CardContent>
            </Card>
          </section>

          <p className="text-xs text-muted-foreground">
            {data.balanced ? "Balanced" : "Unbalanced"} as of {data.as_of}.
          </p>
        </div>
      ) : null}
    </AccountingModuleShell>
  );
}
